import * as THREE from 'three';

export class WarpEffect {
    private scene: THREE.Scene;
    private camera: THREE.PerspectiveCamera;
    private renderer: THREE.WebGLRenderer;
    private stars: THREE.Points;
    private warpLines: THREE.LineSegments;
    private clock: THREE.Clock;
    private container: HTMLElement;
    private canvas: HTMLCanvasElement;

    private isWarping = false;
    private warpSpeed = 0;
    private targetWarpSpeed = 0;
    private onComplete?: () => void;

    // Mercury/MTS inspired colors
    private readonly primaryColor = 0x00d4ff; // Cyan
    private readonly secondaryColor = 0x7c3aed; // Purple

    constructor(container: HTMLElement) {
        this.container = container;
        this.clock = new THREE.Clock();

        // Create canvas
        this.canvas = document.createElement('canvas');
        this.canvas.id = 'warp-canvas';
        this.canvas.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 2000;
    `;
        this.container.appendChild(this.canvas);

        // Initialize Three.js
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x000005);

        this.camera = new THREE.PerspectiveCamera(
            75,
            window.innerWidth / window.innerHeight,
            0.1,
            2000
        );
        this.camera.position.z = 0;

        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: true,
        });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

        // Create effects
        this.stars = this.createStarField();
        this.warpLines = this.createWarpLines();

        // Event listeners
        window.addEventListener('resize', this.onResize.bind(this));

        // Start animation
        this.animate();
    }

    private createStarField(): THREE.Points {
        const starCount = 3000;
        const positions = new Float32Array(starCount * 3);
        const colors = new Float32Array(starCount * 3);
        const sizes = new Float32Array(starCount);

        const colorPrimary = new THREE.Color(this.primaryColor);
        const colorSecondary = new THREE.Color(this.secondaryColor);
        const colorWhite = new THREE.Color(0xffffff);

        for (let i = 0; i < starCount; i++) {
            // Distribute stars in a cylinder around the camera
            const angle = Math.random() * Math.PI * 2;
            const radius = 50 + Math.random() * 200;

            positions[i * 3] = Math.cos(angle) * radius;
            positions[i * 3 + 1] = Math.sin(angle) * radius;
            positions[i * 3 + 2] = Math.random() * 2000 - 1000;

            // Random color between primary, secondary, and white
            const colorChoice = Math.random();
            let color: THREE.Color;
            if (colorChoice < 0.3) {
                color = colorPrimary;
            } else if (colorChoice < 0.5) {
                color = colorSecondary;
            } else {
                color = colorWhite;
            }

            colors[i * 3] = color.r;
            colors[i * 3 + 1] = color.g;
            colors[i * 3 + 2] = color.b;

            sizes[i] = Math.random() * 3 + 1;
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

        const material = new THREE.PointsMaterial({
            size: 2,
            vertexColors: true,
            transparent: true,
            opacity: 0.8,
            blending: THREE.AdditiveBlending,
            sizeAttenuation: true,
        });

        const stars = new THREE.Points(geometry, material);
        this.scene.add(stars);

        return stars;
    }

    private createWarpLines(): THREE.LineSegments {
        const lineCount = 500;
        const positions = new Float32Array(lineCount * 6); // 2 points per line
        const colors = new Float32Array(lineCount * 6);

        const colorPrimary = new THREE.Color(this.primaryColor);
        const colorSecondary = new THREE.Color(this.secondaryColor);

        for (let i = 0; i < lineCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const radius = 20 + Math.random() * 150;
            const z = Math.random() * 1000;

            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;

            // Start point
            positions[i * 6] = x;
            positions[i * 6 + 1] = y;
            positions[i * 6 + 2] = z;

            // End point (will be updated during warp)
            positions[i * 6 + 3] = x;
            positions[i * 6 + 4] = y;
            positions[i * 6 + 5] = z + 5;

            // Colors (gradient from primary to secondary)
            const color = Math.random() > 0.5 ? colorPrimary : colorSecondary;
            colors[i * 6] = color.r;
            colors[i * 6 + 1] = color.g;
            colors[i * 6 + 2] = color.b;
            colors[i * 6 + 3] = color.r * 0.3;
            colors[i * 6 + 4] = color.g * 0.3;
            colors[i * 6 + 5] = color.b * 0.3;
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

        const material = new THREE.LineBasicMaterial({
            vertexColors: true,
            transparent: true,
            opacity: 0,
            blending: THREE.AdditiveBlending,
        });

        const lines = new THREE.LineSegments(geometry, material);
        this.scene.add(lines);

        return lines;
    }

    private onResize(): void {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    private animate(): void {
        requestAnimationFrame(this.animate.bind(this));

        const delta = this.clock.getDelta();
        const elapsed = this.clock.getElapsedTime();

        // Smooth warp speed transition
        this.warpSpeed += (this.targetWarpSpeed - this.warpSpeed) * delta * 2;

        // Update stars
        const starPositions = this.stars.geometry.attributes.position.array as Float32Array;
        for (let i = 0; i < starPositions.length; i += 3) {
            // Move stars toward camera
            starPositions[i + 2] += (5 + this.warpSpeed * 50) * delta * 60;

            // Reset stars that pass the camera
            if (starPositions[i + 2] > 100) {
                starPositions[i + 2] = -1000;
            }
        }
        this.stars.geometry.attributes.position.needsUpdate = true;

        // Update star stretching based on warp speed
        (this.stars.material as THREE.PointsMaterial).size = 2 + this.warpSpeed * 8;

        // Update warp lines
        const linePositions = this.warpLines.geometry.attributes.position.array as Float32Array;
        const lineMaterial = this.warpLines.material as THREE.LineBasicMaterial;

        lineMaterial.opacity = this.warpSpeed * 0.8;

        for (let i = 0; i < linePositions.length; i += 6) {
            // Move lines
            linePositions[i + 2] += (10 + this.warpSpeed * 100) * delta * 60;
            linePositions[i + 5] = linePositions[i + 2] + 5 + this.warpSpeed * 100;

            // Reset lines
            if (linePositions[i + 2] > 100) {
                linePositions[i + 2] = -500;
                linePositions[i + 5] = linePositions[i + 2] + 5;
            }
        }
        this.warpLines.geometry.attributes.position.needsUpdate = true;

        // Camera shake when warping
        if (this.isWarping) {
            const shake = this.warpSpeed * 0.5;
            this.camera.position.x = (Math.random() - 0.5) * shake;
            this.camera.position.y = (Math.random() - 0.5) * shake;

            // Increase FOV during warp for tunnel effect
            this.camera.fov = 75 + this.warpSpeed * 30;
            this.camera.updateProjectionMatrix();
        }

        // Add subtle rotation
        this.stars.rotation.z = elapsed * 0.02;
        this.warpLines.rotation.z = elapsed * 0.02;

        this.renderer.render(this.scene, this.camera);
    }

    public startWarp(onComplete: () => void): void {
        this.onComplete = onComplete;
        this.isWarping = true;

        // Phase 1: Build up (2 seconds)
        this.targetWarpSpeed = 0.3;

        setTimeout(() => {
            // Phase 2: Full warp (1.5 seconds)
            this.targetWarpSpeed = 1.0;

            setTimeout(() => {
                // Phase 3: Flash and complete
                this.flashAndComplete();
            }, 1500);
        }, 2000);
    }

    private flashAndComplete(): void {
        // Create flash overlay
        const flash = document.createElement('div');
        flash.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: radial-gradient(circle, rgba(0, 212, 255, 0.9) 0%, rgba(124, 58, 237, 0.9) 50%, rgba(0, 0, 10, 1) 100%);
      z-index: 2001;
      opacity: 0;
      transition: opacity 0.3s ease-out;
    `;
        document.body.appendChild(flash);

        // Flash in
        requestAnimationFrame(() => {
            flash.style.opacity = '1';
        });

        setTimeout(() => {
            // Flash out and cleanup
            flash.style.opacity = '0';
            this.canvas.style.opacity = '0';
            this.canvas.style.transition = 'opacity 0.5s ease-out';

            setTimeout(() => {
                flash.remove();
                this.dispose();
                if (this.onComplete) {
                    this.onComplete();
                }
            }, 500);
        }, 300);
    }

    public dispose(): void {
        // Remove canvas
        this.canvas.remove();

        // Dispose Three.js resources
        this.stars.geometry.dispose();
        (this.stars.material as THREE.Material).dispose();
        this.warpLines.geometry.dispose();
        (this.warpLines.material as THREE.Material).dispose();
        this.renderer.dispose();

        // Remove event listener
        window.removeEventListener('resize', this.onResize.bind(this));
    }
}
